/* Centralized AudioManager — synthesized SFX via WebAudio (no asset files needed).
   Falls back silently if WebAudio is unavailable. Never requests microphone. */

const LS_KEY = "lostdc_audio";

function loadSettings() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { master: 0.8, music: 0.35, sfx: 0.9, muted: false, ...JSON.parse(raw) };
  } catch (e) {}
  return { master: 0.8, music: 0.35, sfx: 0.9, muted: false };
}

class AudioManager {
  constructor() {
    this.ctx = null;
    this.settings = loadSettings();
    this.musicTimer = null;
    this.musicPlaying = false;
    this._lastPlay = {};
  }

  save() {
    try { localStorage.setItem(LS_KEY, JSON.stringify(this.settings)); } catch (e) {}
  }

  ensure() {
    if (!this.ctx) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        this.ctx = new AC();
      } catch (e) { this.ctx = null; }
    }
    if (this.ctx && this.ctx.state === "suspended") this.ctx.resume();
    return this.ctx;
  }

  set(key, val) {
    this.settings[key] = val;
    this.save();
    if (key === "muted" && val) this.stopMusic();
  }
  get() { return { ...this.settings }; }

  _tone(freq, dur, { type = "sine", vol = 0.5, at = 0, slideTo = null, channel = "sfx" } = {}) {
    const ctx = this.ensure();
    if (!ctx || this.settings.muted) return;
    const gainMul = this.settings.master * (channel === "music" ? this.settings.music : this.settings.sfx);
    if (gainMul <= 0) return;
    const t0 = ctx.currentTime + at;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(Math.max(0.001, vol * gainMul), t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g); g.connect(ctx.destination);
    osc.start(t0); osc.stop(t0 + dur + 0.03);
  }

  _noise(dur, { vol = 0.3, at = 0, channel = "sfx" } = {}) {
    const ctx = this.ensure();
    if (!ctx || this.settings.muted) return;
    const gainMul = this.settings.master * this.settings.sfx;
    if (gainMul <= 0) return;
    const t0 = ctx.currentTime + at;
    const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = ctx.createBufferSource(); src.buffer = buf;
    const g = ctx.createGain(); g.gain.value = vol * gainMul;
    src.connect(g); g.connect(ctx.destination); src.start(t0);
  }

  play(name) {
    // light throttle to prevent duplicate spam
    const now = Date.now();
    if (this._lastPlay[name] && now - this._lastPlay[name] < 40) return;
    this._lastPlay[name] = now;
    const P = this.presets[name];
    if (P) { try { P.call(this); } catch (e) {} }
  }

  get presets() {
    return {
      hover: () => this._tone(660, 0.05, { type: "sine", vol: 0.12 }),
      click: () => this._tone(420, 0.08, { type: "triangle", vol: 0.4, slideTo: 620 }),
      createRoom: () => { this._tone(523, 0.1, { type: "triangle", vol: 0.4 }); this._tone(784, 0.14, { at: 0.09, vol: 0.4 }); },
      joinRoom: () => { this._tone(494, 0.1, { type: "triangle", vol: 0.4 }); this._tone(659, 0.14, { at: 0.09, vol: 0.4 }); },
      playerJoin: () => this._tone(880, 0.1, { type: "sine", vol: 0.35, slideTo: 1100 }),
      playerLeave: () => this._tone(500, 0.14, { type: "sine", vol: 0.3, slideTo: 300 }),
      gameStart: () => { [523, 659, 784, 1046].forEach((f, i) => this._tone(f, 0.16, { at: i * 0.1, vol: 0.4, type: "triangle" })); },
      countdown: () => this._tone(700, 0.08, { type: "square", vol: 0.25 }),
      drawStart: () => { this._tone(300, 0.1, { vol: 0.3, slideTo: 700, type: "sawtooth" }); this._noise(0.1, { vol: 0.12 }); },
      drawEnd: () => this._tone(600, 0.2, { type: "square", vol: 0.3, slideTo: 200 }),
      voteSelect: () => this._tone(760, 0.06, { type: "sine", vol: 0.3 }),
      voteLock: () => { this._tone(520, 0.08, { vol: 0.4, type: "triangle" }); this._tone(760, 0.1, { at: 0.07, vol: 0.4 }); },
      hotSeat: () => { this._tone(140, 1.1, { type: "sawtooth", vol: 0.28, slideTo: 320 }); this._noise(0.5, { vol: 0.1, at: 0.6 }); },
      defenseStart: () => this._tone(392, 0.2, { type: "triangle", vol: 0.35, slideTo: 523 }),
      impact: () => { this._tone(90, 0.3, { type: "square", vol: 0.5 }); this._noise(0.25, { vol: 0.3 }); },
      elimination: () => { this._tone(400, 0.15, { type: "sawtooth", vol: 0.4, slideTo: 120 }); this._tone(120, 0.35, { at: 0.13, vol: 0.35 }); },
      roleReveal: () => { this._tone(660, 0.12, { type: "triangle", vol: 0.4 }); this._tone(990, 0.18, { at: 0.1, vol: 0.4 }); },
      imposterCaught: () => { [784, 988, 1318].forEach((f, i) => this._tone(f, 0.18, { at: i * 0.11, vol: 0.4, type: "triangle" })); },
      imposterSurvive: () => { this._tone(300, 0.25, { type: "sawtooth", vol: 0.35, slideTo: 500 }); this._tone(500, 0.2, { at: 0.2, vol: 0.3 }); },
      misdirector: () => { [660, 550, 660, 880].forEach((f, i) => this._tone(f, 0.16, { at: i * 0.12, vol: 0.4, type: "sine" })); },
      score: () => this._tone(1046, 0.06, { type: "sine", vol: 0.3, slideTo: 1568 }),
      survive: () => { this._tone(523, 0.12, { vol: 0.35, type: "sine" }); this._tone(784, 0.16, { at: 0.1, vol: 0.35 }); },
      winner: () => { [523, 659, 784, 1046, 1318].forEach((f, i) => this._tone(f, 0.22, { at: i * 0.13, vol: 0.45, type: "triangle" })); },
      playAgain: () => { this._tone(659, 0.1, { vol: 0.4 }); this._tone(880, 0.14, { at: 0.09, vol: 0.4 }); },
      error: () => this._tone(200, 0.2, { type: "square", vol: 0.3, slideTo: 140 }),
      success: () => { this._tone(660, 0.1, { vol: 0.35 }); this._tone(990, 0.12, { at: 0.08, vol: 0.35 }); },
    };
  }

  startMusic() {
    if (this.musicPlaying || this.settings.muted) return;
    const ctx = this.ensure();
    if (!ctx) return;
    this.musicPlaying = true;
    const chords = [
      [261.6, 329.6, 392.0],
      [293.7, 349.2, 440.0],
      [220.0, 277.2, 329.6],
      [246.9, 311.1, 392.0],
    ];
    let i = 0;
    const loop = () => {
      if (!this.musicPlaying || this.settings.muted) return;
      const c = chords[i % chords.length];
      c.forEach((f) => this._tone(f, 2.0, { type: "sine", vol: 0.06, channel: "music" }));
      i++;
      this.musicTimer = setTimeout(loop, 2100);
    };
    loop();
  }

  stopMusic() {
    this.musicPlaying = false;
    if (this.musicTimer) { clearTimeout(this.musicTimer); this.musicTimer = null; }
  }
}

export const audio = new AudioManager();

// resume on first gesture
if (typeof window !== "undefined") {
  const kick = () => { audio.ensure(); window.removeEventListener("pointerdown", kick); };
  window.addEventListener("pointerdown", kick);
}
